import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import KC_Button, {
  KC_ButtonSize
} from "../../../../../designsystem/KC_Button/KC_Button";
import Carousel from "../../../../../components/Carousel/Carousel";
import GLMCard from "./GLMCard";
import { useAccountToDisplaySelector } from "../../../../../redux/actions/user";
import { updateJetpackConfigThunk } from "../../../../../redux/jetpackConfig";
import "../styles.scss";
import NiceModal from "@ebay/nice-modal-react";
import PlanOptionModal from "../../../../../components/modal/PlanOption/PlanOptionModal";
import { tt, LANGUAGE } from "../../../../../utils/i18n/i18n";
import KC_Heading2 from "../../../../../designsystem/typography/KC-Heading/KC_Heading2";
import KC_Text from "../../../../../designsystem/typography/KC_Text/KC_Text";
import KC_Toggle from "../../../../../designsystem/KC_FormInput/KC_Toggle";
import { MonthlyLottery } from "kadow-model-monthly-lottery";
import PrizesConfiguratorDisplayCard from "../../GiftsConfiguration/component/PrizesConfiguratorDisplayCard";
import KC_PrizeIcon from "../../../../../designsystem/KC_PrizeIcon/KC_PrizeIcon";
import TSUtils from "../../../../../utils/helpers/TSUtils";
import type { Config } from "kadow-jetpack-model";

interface Props {
  glmList: MonthlyLottery[];
  config?: Config;
  setFieldValue?: (field: string, value: any) => void;
}
export default function MLPreview(props: Props) {
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();

  const isUpsell = useMemo(() => {
    return (
      !!accountToDisplay?.saasFeatures && !accountToDisplay?.saasFeatures?.ml
    );
  }, [accountToDisplay]);

  const isFormikControlled = useMemo(
    () => !!props.config && !!props.setFieldValue,
    [props.config, props.setFieldValue]
  );

  const isMonthlyLotteryEnabled = useMemo(() => {
    if (isFormikControlled) {
      return !(props.config?.dml ?? false);
    }
    return true;
  }, [isFormikControlled, props.config]);

  const onToggle = () => {
    const currentDml = props.config?.dml ?? false;
    const newDml = !currentDml;

    if (isFormikControlled && props.setFieldValue) {
      props.setFieldValue("dml", newDml);
    }

    dispatch(
      updateJetpackConfigThunk(
        { dml: newDml },
        accountToDisplay?.trackingID,
        accountToDisplay?.token
      )
    );
  };

  const filterGLM = (idx: number, isUpsell: boolean) => {
    if (!isUpsell) {
      return true;
    }
    return idx < 6;
  };

  const filteredGLMList = useMemo(
    () => props.glmList.filter((_, idx) => filterGLM(idx, isUpsell)),
    [props.glmList, isUpsell]
  );

  const mlSlides = useMemo(() => {
    return filteredGLMList.map((glm) => {
      if (isUpsell) {
        return (
          <div key={glm._id} className="prize-preview-container">
            <GLMCard glm={glm} isUpsell={true} />
          </div>
        );
      }
      const dateText =
        glm.minTs && glm.maxTs && glm.minTs > 0 && glm.maxTs > 0
          ? TSUtils.formatDateRange(glm.minTs, glm.maxTs)
          : TSUtils.getGLMPeriod(glm);
      const title = glm.prizeL?.[LANGUAGE] || glm.prize;
      const media =
        glm.img != null && glm.img !== "" ? (
          <img
            src={`https://tootsweet.twic.pics/cdm/kadow/glm/${glm.img}`}
            style={{ width: "50%" }}
            alt=""
          />
        ) : (
          <KC_PrizeIcon emoji={glm.e ?? "🚀"} />
        );
      return (
        <div key={glm._id} className="prize-preview-container">
          <PrizesConfiguratorDisplayCard
            title={title}
            media={media}
            className="prizes-configurator-display-card--ml flex-grow-1"
          >
            <div className="d-flex align-items-center">
              <span className="text-blue p-1">
                <FontAwesomeIcon icon={faCalendar} />
              </span>
              <span className="prizes-configurator-display-card__secondary prizes-configurator-display-card__secondary--ml text-blue text-capitalize">
                {dateText}
              </span>
            </div>
          </PrizesConfiguratorDisplayCard>
        </div>
      );
    });
  }, [filteredGLMList, isUpsell]);

  const BUTTONS = useMemo(() => {
    return (
      <>
        <div className="d-none d-sm-block">
          <KC_Button
            size={KC_ButtonSize.Large}
            isUpsell={true}
            text={tt("upsell_premium_button")}
            onClick={() => NiceModal.show(PlanOptionModal)}
            className="d-flex"
          />
        </div>
        <div className="d-block d-sm-none w-100">
          <KC_Button
            size={KC_ButtonSize.Large}
            isUpsell={true}
            text={tt("upsell_premium_button")}
            onClick={() => NiceModal.show(PlanOptionModal)}
            className="d-flex"
          />
        </div>
      </>
    );
  }, []);

  const displayCarousel = useMemo(() => {
    if (isUpsell) {
      return true;
    }

    return isMonthlyLotteryEnabled;
  }, [isUpsell, isMonthlyLotteryEnabled]);

  return (
    <section
      className={`prizes-config-section w-100 ${isUpsell ? "upsell-glm p-4" : ""}`}
    >
      <div className="prizes-config-section__header">
        <div className="prizes-config-section__title-block prizes-configurator-heading-block">
          <div className="prizes-config-section__title-row">
            <KC_Heading2
              text={tt("monthly_lottery")}
              className={isUpsell ? "text-upsell" : ""}
            />
            {!isUpsell && (
              <KC_Toggle
                id="toggle-glm"
                isChecked={isMonthlyLotteryEnabled}
                onToggle={onToggle}
              />
            )}
          </div>
          {!isUpsell && (
            <KC_Text
              size="lg"
              weight="reg"
              text={tt("monthly_lottery_description")}
              className="text-secondary"
            />
          )}
        </div>
        {isUpsell ? (
          <div className="prizes-config-section__actions">{BUTTONS}</div>
        ) : null}
      </div>
      {displayCarousel && (
        <>
          <div className="prizes-config-section__content ml-preview-carousel-wrapper">
            <Carousel
              slides={mlSlides}
              className="prize-slides flex-grow-1"
              baseId="ml"
              enableNavigationAllBreakpoints
            />
          </div>
        </>
      )}
    </section>
  );
}
